/**
 * LES ROUTES DU MODULE SOUS LA FICHE D'UN ÉQUIPEMENT (LOT F3).
 * ============================================================================
 *
 * Trois enfants de la coquille de la fiche, pour une source
 * (`core/router/source.routes.ts`, écrite à la main) et pour un bâtiment
 * (`building.routes.ts` du module, lu par le générateur du routeur) :
 *  • `surveillance` : l'onglet, la liste filtrée sur l'équipement ;
 *  • `surveillance/:_id_instance` : la fiche d'une surveillance ;
 *  • `surveillance/:_id_instance/alarme/:_id_occurrence` : le détail d'une
 *    alarme, la seule page qui lit la route 12 (D71).
 * Aucun chemin ni nom de la V1 n'est repris. Chaque route enregistre les
 * messages du module puis attend la porte (`utils/porte.ts`), comme la liste.
 */
import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router"
import PageSurveillancesEquipement from "@modules/alarmes-v2/pages/PageSurveillancesEquipement.vue"
import PageSurveillanceFiche from "@modules/alarmes-v2/pages/PageSurveillanceFiche.vue"
import PageSurveillanceAlarme from "@modules/alarmes-v2/pages/PageSurveillanceAlarme.vue"
import { enregistrerMessagesAlarmesV2 } from "@modules/alarmes-v2/i18n/enregistrer"
import { porteAlarmesV2 } from "./porte"
import { SEGMENT_SURVEILLANCE } from "./menu"

/** Enregistre les messages, puis juge l'entrée. */
async function entrer() {
    enregistrerMessagesAlarmesV2()
    return porteAlarmesV2()
}

/** Le genre d'équipement et le nom de son paramètre d'adresse. */
const PARAMETRE = { source: "_id_source", building: "_id_building" } as const

/** Le préfixe des noms de route (`PageSourceSurveillance…`). */
const PREFIXE = { source: "PageSource", building: "PageBuilding" } as const

/** Une valeur de paramètre d'adresse, en chaîne. */
const param = (to: RouteLocationNormalized, nom: string): string =>
    String(to.params[nom] ?? "")

/**
 * Les trois enfants de la coquille d'un équipement.
 * @param objetType - `source` ou `building`
 */
export function enfantsSurveillance(
    objetType: "source" | "building"
): RouteRecordRaw[] {
    const id = PARAMETRE[objetType]
    const meta = { documentTitle: "Surveillance" }
    return [
        {
            path: SEGMENT_SURVEILLANCE,
            name: `${PREFIXE[objetType]}Surveillances`,
            component: PageSurveillancesEquipement,
            meta,
            beforeEnter: entrer,
            props: (to) => ({ objetType, idObjet: param(to, id) }),
        },
        {
            path: `${SEGMENT_SURVEILLANCE}/:_id_instance`,
            name: `${PREFIXE[objetType]}SurveillanceFiche`,
            component: PageSurveillanceFiche,
            meta,
            beforeEnter: entrer,
            props: (to) => ({
                objetType,
                idObjet: param(to, id),
                idInstance: param(to, "_id_instance"),
            }),
        },
        {
            path: `${SEGMENT_SURVEILLANCE}/:_id_instance/alarme/:_id_occurrence`,
            name: `${PREFIXE[objetType]}SurveillanceAlarme`,
            component: PageSurveillanceAlarme,
            meta,
            beforeEnter: entrer,
            props: (to) => ({
                objetType,
                idObjet: param(to, id),
                idInstance: param(to, "_id_instance"),
                idAlarme: param(to, "_id_occurrence"),
            }),
        },
    ]
}
